import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Alert
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import api from '../services/api';

function StatusLista() {
    const navigate = useNavigate();
    const [statusList, setStatusList] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ valor: '', ativo: true, ordem: 0 });
    const [error, setError] = useState('');

    useEffect(() => {
        loadStatusList();
    }, []);

    const loadStatusList = async () => {
        setLoading(true);
        try {
            const response = await api.get('/status-lista');
            setStatusList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de status:', error);
            setError('Erro ao carregar lista de status');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ valor: item.valor, ativo: item.ativo === 1, ordem: item.ordem || 0 });
        } else {
            setEditingItem(null);
            setFormData({ valor: '', ativo: true, ordem: 0 });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ valor: '', ativo: true, ordem: 0 });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.valor.trim()) {
            setError('Valor é obrigatório');
            return;
        }

        try {
            if (editingItem) {
                await api.put(`/status-lista/${editingItem.id}`, formData);
            } else {
                await api.post('/status-lista', formData);
            }
            handleCloseDialog();
            loadStatusList();
        } catch (error) {
            console.error('Erro ao salvar status:', error);
            setError(error.response?.data?.error || 'Erro ao salvar status');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este status?')) {
            try {
                await api.delete(`/status-lista/${id}`);
                loadStatusList();
            } catch (error) {
                console.error('Erro ao deletar status:', error);
                alert('Erro ao deletar status: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const statusFiltrados = statusList.filter(s => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return s.valor.toLowerCase().includes(searchLower);
    });

    return (
        <Container>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">
                    Gerenciar Status
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Novo Status
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar status..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Valor</TableCell>
                            <TableCell align="center">Ativo</TableCell>
                            <TableCell align="center">Ordem</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : statusFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {search ? 'Nenhum status encontrado' : 'Nenhum status cadastrado'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            statusFiltrados.map((status) => (
                                <TableRow key={status.id} hover>
                                    <TableCell>{status.valor}</TableCell>
                                    <TableCell align="center">
                                        {status.ativo === 1 ? '✓' : '✗'}
                                    </TableCell>
                                    <TableCell align="center">{status.ordem || 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(status)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(status.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Status' : 'Novo Status'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Valor"
                            value={formData.valor}
                            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Ordem"
                            type="number"
                            value={formData.ordem}
                            onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.ativo}
                                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                />
                            }
                            label="Ativo"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default StatusLista;

